import { Capacitor } from "@capacitor/core";
import axios from "axios";

import { resolveApiEnvironment } from "./apiEnvironment";
import { platformRef } from "../platform/platformRef";

/**
 * Resolution order (see apiEnvironment.ts): native runtime environment
 * override > build-time REACT_APP_NATIVE_API_ORIGIN > prod fallback.
 */
function getDefaultApiOrigin(): string {
    const resolved = resolveApiEnvironment();
    if (
        resolved.source === "default" &&
        Capacitor.isNativePlatform() &&
        process.env.NODE_ENV === "production"
    ) {
        // Expected on every real TestFlight/App Store boot — production
        // distribution builds intentionally ship without
        // REACT_APP_NATIVE_API_ORIGIN set and fall back to prod here (see
        // apiEnvironment.ts). Local dev Xcode builds land here too if the env
        // var isn't set for them; the runtime environment pickers can repoint
        // either case, so this is informational, not an error.
        console.info(`REACT_APP_NATIVE_API_ORIGIN is not set; defaulting to ${resolved.origin}`);
    }
    return resolved.origin;
}

/**
 * The native backend origin, resolved once per page load. A picker change
 * requires a reload. Plain web remains same-origin.
 */
let resolvedOrigin: string | null | undefined;

function apiOrigin(): string | null {
    if (resolvedOrigin === undefined) {
        resolvedOrigin = Capacitor.isNativePlatform() ? getDefaultApiOrigin() : null;
    }
    return resolvedOrigin;
}

/**
 * In the native shell the web bundle is served from capacitor://localhost, so
 * relative /api URLs resolve nowhere. Point axios and relative fetch() calls at
 * a real backend origin instead. Web requests always stay same-origin.
 */
export function configureNativeApiOrigin(): void {
    const origin = apiOrigin();
    if (!origin) {
        return;
    }
    axios.defaults.baseURL = origin;
    // ngrok free-tier tunnels answer browser-UA requests with an interstitial
    // page unless this header is present — WKWebView has a Safari UA, so
    // tunneled dev backends would break without it. Harmless for non-ngrok
    // backends (native requests are already CORS-preflighted anyway).
    axios.defaults.headers.common["ngrok-skip-browser-warning"] = "true";
    const nativeFetch = window.fetch.bind(window);
    window.fetch = (input, init) => {
        if (typeof input === "string" && input.startsWith("/api/")) {
            // API-bound raw fetch (e.g. the SSE run stream, which bypasses
            // axios and its interceptors) — needs the same ngrok header as
            // the axios default above, or the tunnel's interstitial kills
            // the request before it reaches the backend.
            const headers = new Headers(init?.headers);
            headers.set("ngrok-skip-browser-warning", "true");
            return nativeFetch(origin + input, { ...init, headers });
        }
        return nativeFetch(input, init);
    };
}

/**
 * Prefix an API path with the origin that the host resolved during bootstrap.
 * The web host installs an empty origin, so web requests stay relative.
 */
export function apiUrl(path: string): string {
    return platformRef.current.bootstrap.apiOrigin + path;
}

/**
 * The backend origin the native shell is configured to hit — same value
 * `configureNativeApiOrigin` set as axios's baseURL. Used by native-aware code
 * (e.g. NativeNavBridge, ViewerApi callers) that needs to pass an explicit
 * origin to native plugins rather than relying on axios's relative-URL config.
 */
export function getNativeApiOrigin(): string {
    return apiOrigin() ?? getDefaultApiOrigin();
}
